import type { Car, Entry } from "./types";

export type PlanId = "free" | "enthusiast" | "shop";

/**
 * What the account may use, as the account function reports it (`ent` in the current app).
 * Worked out on the server from licences and add-ons; the app only reads it.
 * `cars: null` = unlimited. `logs` is the entry limit; missing or null = unlimited.
 */
export interface Entitlement {
  plan: PlanId;
  devices: number;
  cars: number | null;
  logs?: number | null;
  baseDevices?: number;
  extraDevices?: number;
  baseCars?: number | null;
  extraCars?: number;
  recurring?: boolean;
  trial?: unknown;
}

/** The plan in use; Enthusiast when there is no entitlement yet (a preview with no backend). */
export const planOf = (ent: Entitlement | null | undefined): PlanId => ent?.plan ?? "enthusiast";

/** Cars that count toward the limit (sample cars never do). */
export const ownCars = (cars: readonly Pick<Car, "sample">[]): number => cars.filter((c) => !c.sample).length;

/** How many more cars may be added (Infinity when unlimited or not known yet). */
export function carRoom(ent: Entitlement | null | undefined, cars: readonly Pick<Car, "sample">[]): number {
  if (!ent || ent.cars == null) return Infinity;
  return ent.cars - ownCars(cars);
}

/** Entries that count toward the limit (sample entries never do). */
export const ownEntries = (log: readonly Pick<Entry, "sample">[]): number => log.filter((e) => !e.sample).length;

/** How many more entries may be added (Infinity when unlimited or not known yet). */
export function logRoom(ent: Entitlement | null | undefined, log: readonly Pick<Entry, "sample">[]): number {
  if (!ent || ent.logs == null) return Infinity;
  return ent.logs - ownEntries(log);
}

export const PLAN_NAME: Readonly<Record<PlanId, string>> = { free: "Free", enthusiast: "Enthusiast", shop: "Shop" };

/** What each plan gives in the new app, for the plan screens. The server enforces these. */
export const PLAN_LIMITS = {
  free: { cars: 1, logs: 10, devices: 1 },
  enthusiast: { cars: 6, logs: null, devices: 1 },
  shop: { cars: null, logs: null, devices: 3 },
} as const;
